import { useMemo, useState } from "react";
import type { TypedWrappedFieldProps, WrappedFieldProps } from "redux-form";

import { Box, Button } from "@braintrust/braintrust-ui-components";
import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";

import { getPaymentTypeOrder } from "./helpers";

type PaymentTypeFieldProps = {
  job_type: WrappedFieldProps;
  payment_type: WrappedFieldProps;
  talent_invoice_editing_disabled: TypedWrappedFieldProps<boolean>;
};

export const PaymentTypeField = ({
  job_type,
  payment_type,
  talent_invoice_editing_disabled,
}: PaymentTypeFieldProps) => {
  const isNodeOrImpersonated = useIsNodeOrImpersonated();
  const selectedPaymentType: EnumType<typeof ENUMS.JobPaymentType> =
    payment_type.input.value;
  const [initiallySelectedPaymentType] = useState(() => selectedPaymentType);

  const isFixedProjectInitiallySelected =
    initiallySelectedPaymentType === ENUMS.JobPaymentType.FIXED_PROJECT;
  const isPerTaskInitiallySelected =
    initiallySelectedPaymentType === ENUMS.JobPaymentType.PER_TASK;

  const isTalentInvoiceEditingChecked =
    !!talent_invoice_editing_disabled.input.value;
  // temp type until form not typed properly

  const isFreelanceJobTypeSelected =
    job_type.input.value === ENUMS.JobType.FREELANCE;
  const showFixedProjectType =
    (isNodeOrImpersonated && isFreelanceJobTypeSelected) ||
    isFixedProjectInitiallySelected;
  const showPerTaskType =
    isFreelanceJobTypeSelected || isPerTaskInitiallySelected;

  const options = useMemo(() => {
    const order = getPaymentTypeOrder(
      job_type.input.value as EnumType<typeof ENUMS.JobType>,
    );

    return order
      .filter((paymentType) => {
        if (
          paymentType === ENUMS.JobPaymentType.FIXED_PROJECT &&
          !showFixedProjectType
        ) {
          return false;
        }
        if (paymentType === ENUMS.JobPaymentType.PER_TASK && !showPerTaskType) {
          return false;
        }

        return true;
      })
      .map((value) => {
        return {
          value,
          label: ENUMS.JobPaymentTypeLabels[value],
        };
      });
  }, [job_type.input.value, showFixedProjectType, showPerTaskType]);

  return (
    <Box sx={{ display: "flex", marginLeft: -0.75 }}>
      {options.map((option) => (
        <Box key={option.value}>
          <Button
            variant={
              payment_type.input.value === option.value
                ? "transparent"
                : "transparent-grey"
            }
            className="fw-400"
            shape="squared"
            onClick={() => {
              payment_type.input.onChange(option.value);
              const isFixedProjectOptionSelected =
                option.value === ENUMS.JobPaymentType.FIXED_PROJECT;
              const shouldUncheckTalentInvoiceEditing =
                isTalentInvoiceEditingChecked && !isFixedProjectOptionSelected;
              if (shouldUncheckTalentInvoiceEditing) {
                talent_invoice_editing_disabled.input.onChange(false);
              }
            }}
            style={{ padding: "6px 8px" }}
          >
            {option.label}
          </Button>
        </Box>
      ))}
    </Box>
  );
};
